import { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AdminRole } from "@shared/schema";

interface ImpersonationStatus {
  isImpersonating: boolean;
  impersonatedRole?: AdminRole;
  startedAt?: string;
}

interface AdminRoleContextValue {
  role: AdminRole | null;
  permissions: string[];
  hasAdminAccess: boolean;
  isLoading: boolean;
  hasPermission: (permission: string | string[]) => boolean;
  impersonation: ImpersonationStatus;
  refetch: () => void;
}

const AdminRoleContext = createContext<AdminRoleContextValue | null>(null);

interface AdminRoleProviderProps {
  children: React.ReactNode;
}

export function AdminRoleProvider({ children }: AdminRoleProviderProps) {
  const { data: roleData, isLoading: roleLoading, refetch } = useQuery<{
    role: AdminRole | null;
    permissions: string[];
    hasAdminAccess: boolean;
  }>({
    queryKey: ["/api/admin/my-role"],
  });

  const { data: impersonationData, isLoading: impersonationLoading } = useQuery<ImpersonationStatus>({
    queryKey: ["/api/admin/impersonate/status"],
  });

  const value = useMemo<AdminRoleContextValue>(() => {
    const role = roleData?.role ?? null;
    const permissions = roleData?.permissions ?? [];
    const hasAdminAccess = roleData?.hasAdminAccess ?? false;

    const hasPermission = (permission: string | string[]): boolean => {
      if (!hasAdminAccess) return false;
      // Users with isAdmin flag but no specific role have full access (legacy super admin)
      if (hasAdminAccess && !role && permissions.length === 0) {
        return true;
      }
      const permArray = Array.isArray(permission) ? permission : [permission];
      return permArray.some((p) => permissions.includes(p));
    };

    const impersonation: ImpersonationStatus = impersonationData ?? {
      isImpersonating: false,
    };

    return {
      role,
      permissions,
      hasAdminAccess,
      isLoading: roleLoading || impersonationLoading,
      hasPermission,
      impersonation,
      refetch,
    };
  }, [roleData, impersonationData, roleLoading, impersonationLoading, refetch]);

  return (
    <AdminRoleContext.Provider value={value}>
      {children}
    </AdminRoleContext.Provider>
  );
}

export function useAdminRole() {
  const context = useContext(AdminRoleContext);
  if (!context) {
    throw new Error("useAdminRole must be used within an AdminRoleProvider");
  }
  return context;
}

export function useHasPermission(permission: string | string[]): boolean {
  const { hasPermission } = useAdminRole();
  return hasPermission(permission);
}
